<template>
  <div class="user-list">

    <v-row>
      <v-col class="text-right">
        <v-btn :to="addRoute" small>
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('user.add') }}
        </v-btn>
        <audit-trail-button class="ml-2" entity-type="User" :organization-id="organizationId" />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <search-field
            solo
            :search.sync="filters.query"
            :placeholder="$t('user.search-user')"
            prepend-inner-icon="ico-magnify"
        >
        </search-field>

        <v-card class="mt-6">
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <form-field
                    :label="$t('user.filter-role')"
                    type="select"
                    :items="roles"
                    v-model="filters.roles"
                    multiple
                    chips
                    deletable-chips
                    clearable
                />
              </v-col>
              <v-col cols="12" md="6">
                <form-field
                    :label="$t('user.filter-status')"
                    type="select"
                    :items="statuses"
                    v-model="filters.status"
                    clearable
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <filter-by-letter
            :selectedLetter="filters.startBy"
            @update:startBy="letter => filters.startBy = letter"
        />
      </v-col>
    </v-row>

    <v-row no-gutters class="mt-10">
      <v-col>
        <v-data-table
            class="user-table"
            :headers="headers"
            :loading="loading"
            :items="users"
            :options.sync="options"
            :server-items-length="total"
            @click:row="openUser"
        >
          <template #item.lastname="{item}">
            {{ fullname(item) }}
            <v-chip v-if="item.ownAccount" x-small class="ml-2">{{ $t('user.you') }}</v-chip>
          </template>

          <!-- Un vrai lien : clic droit, nouvel onglet. -->
          <template #item.email="{item}">
            <router-link :to="userRoute(item)" @click.native.stop>{{ item.email }}</router-link>
          </template>

          <template #item.organizations="{value}">
            <v-tooltip v-for="organization in value" :key="organization.id" top>
              <template v-slot:activator="{ on, attrs }">
                <v-chip x-small class="mr-1 my-1" v-bind="attrs" v-on="on">{{ organization.name }}</v-chip>
              </template>
              <span>{{ organization.path }}</span>
            </v-tooltip>
          </template>

          <template #item.role="{value}">
            {{ $t('enums.role.' + value) }}
          </template>

          <template #item.lastLoginAt="{value}">
            {{ value ? formatDate(value, $t('format_datetime')) : '-' }}
          </template>

          <template #item.status="{item}">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small :color="statusIcons[statusOf(item)].color" v-bind="attrs" v-on="on">{{ statusIcons[statusOf(item)].icon }}</v-icon>
              </template>
              <span>{{ $t('user.statuses.' + statusOf(item)) }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import SecurityRepository from "@repository/SecurityRepository";
import FilterByLetter from "@blocks/FilterByLetter.vue";

/**
 * Les gestionnaires : page Gestionnaires (paramètres) et onglet d'une organisation, seul le filtre d'organisation change.
 * Sa propre ligne est marquée « Vous » ; elle s'ouvre comme les autres, ses droits y sont grisés.
 */
export default {
  components: {AuditTrailButton, FilterByLetter},

  props: {
    organizationId: String,
  },

  data() {
    return {
      // Par défaut, les gestionnaires actifs seulement.
      filters: {
        query: null,
        startBy: null,
        roles: [],
        status: 'active',
      },
      roles: [],
      statuses: ['active', 'disabled', 'pending'].map(value => ({
        text: this.$t('user.status-filters.' + value),
        value,
      })),
      loading: false,
      options: null,
      total: 0,
      users: [],
      headers: [
        {text: this.$t('name'), value: 'lastname'},
        {text: this.$t('email'), value: 'email'},
        {text: this.$t('organization.organization'), value: 'organizations', sortable: false},
        {text: this.$t('role'), value: 'role'},
        {text: this.$t('user.last-login'), value: 'lastLoginAt', width: 160},
        {text: this.$t('user.status'), value: 'status', sortable: false, align: 'center', width: 90},
      ],
      statusIcons: {
        active: {icon: 'mdi-check-circle', color: 'success'},
        disabled: {icon: 'mdi-close-circle', color: 'error'},
        pending: {icon: 'mdi-email-clock-outline', color: 'warning'},
      },
    }
  },

  computed: {
    addRoute() {
      return this.organizationId
          ? {name: 'user.add', query: {oid: this.organizationId}}
          : {name: 'user.add'}
    },
  },

  watch: {
    options() {
      this.loadData()
    },
    filters: {
      handler() {
        if (this.options) {
          this.options = {...this.options, page: 1}
        }
      },
      deep: true,
    },
    organizationId() {
      if (this.options) {
        this.options = {...this.options, page: 1}
      }
    },
  },

  mounted() {
    // Les rôles dont je vois les gestionnaires : les rôles ZEborne n'apparaissent qu'à ceux qui les voient.
    SecurityRepository.roles({visible: 1}).then(roles => {
      this.roles = roles.map(role => ({
        text: this.$t('enums.role.' + role),
        value: role,
      }))
    }).catch(err => {
      this.notifyError(err)
    })
  },

  methods: {
    loadData() {
      const data = {...this.filters}
      if (this.organizationId) {
        data.organizationIds = [this.organizationId]
      }
      if (this.options.itemsPerPage > -1) {
        data.offset = (this.options.page * this.options.itemsPerPage) - this.options.itemsPerPage
      }
      data.limit = this.options.itemsPerPage
      if (this.options.sortBy.length > 0) {
        data.orderBy = this.options.sortBy[0]
      }
      if (this.options.sortDesc.length > 0) {
        data.orderDirection = this.options.sortDesc[0] ? 'desc' : 'asc'
      }

      this.loading = true
      SecurityRepository.search(data).then(response => {
        this.total = response.total
        this.users = response.items
        this.loading = false
      }).catch(e => {
        if (e?.name === 'AbortError' || e?.name === 'CanceledError') return
        this.notifyError(e)
        this.loading = false
      })
    },

    userRoute(user) {
      return {name: 'user.edit', params: {id: user.id}}
    },

    openUser(user) {
      this.$router.push(this.userRoute(user))
    },

    // Un serveur pas encore à jour n'envoie pas `status` : on le déduit de enabled / activated.
    statusOf(item) {
      if (this.statusIcons[item.status]) return item.status
      if (!item.enabled) return 'disabled'
      return item.activated === false ? 'pending' : 'active'
    },

    fullname(item) {
      const items = [item.firstname, item.lastname].filter(value => value)
      return items.length ? items.join(' ') : '#NA'
    },
  },
}
</script>

<style lang="scss">
.user-table {
  tbody tr {
    cursor: pointer;
  }
}
</style>
